import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSmartTTS } from '../hooks/useSmartTTS';
import apiService from '../services/apiService';
import { ArrowLeft, Volume2, Check, X, RefreshCw, BrainCircuit } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const QuizContainer = styled.div`
  background: #141414; /* Deep Void */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: 0;
  color: #e5e5e5;
  position: relative;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
`;

const ModeBadge = styled.div`
    background: rgba(229, 9, 20, 0.2);
    color: #e50914;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 6px;
    border: 1px solid rgba(229, 9, 20, 0.3);
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #a3a3a3;
  cursor: pointer;
  padding: 8px;
  transition: color 0.2s;

  &:hover {
    color: white;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: #333;
  position: absolute;
  top: 0;
  left: 0;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #e50914;
  width: ${props => props.$percent}%;
  transition: width 0.3s ease;
`;

const CardContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px; /* Reduced from 20px */
  perspective: 1000px; /* For 3D flip effect */
`;

/* Flip Logic */
const Card = styled.div`
  width: 100%;
  max-width: 400px;
  aspect-ratio: 3/4;
  background: transparent;
  position: relative;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  transform: ${props => props.$flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'};
  cursor: pointer;
  margin-bottom: 0; /* Ensure no extra margin */
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 20px;
  background: #141414;
  border: 1px solid #333;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px; /* Reduced from 30px */
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  background: #222;
  border-color: #e50914; /* Red border for answer side */
`;

const WordTitle = styled.h1`
  font-size: 2.5rem; /* Reduced from 3.5rem to prevent wrapping (approx 40px) */
  font-weight: 800;
  color: #fff;
  margin-bottom: 20px;
  text-align: center;
  word-break: break-word;
`;

const MeaningText = styled.h2`
  font-size: 1.5rem; /* Reduced for better balance */
  font-weight: 700;
  color: #e50914;
  margin-bottom: 20px;
  text-align: center;
`;

const ExampleText = styled.p`
  font-size: 1rem;
  color: #a3a3a3;
  text-align: center;
  line-height: 1.5;
  margin-top: 20px;
  font-style: italic;
`;

const HintText = styled.p`
  margin-top: auto;
  color: #666;
  font-size: 0.8rem;
`;

const Controls = styled.div`
  padding: 10px 20px 30px; /* Top 10, Side 20, Bottom 30 */
  display: flex;
  justify-content: center;
  gap: 12px;
  width: 100%;
  max-width: 400px; /* Limit width */
  margin: 0 auto;
`;

const ControlButton = styled.button`
  flex: 1;
  height: 54px; /* Reduced height */
  border-radius: 12px;
  border: none;
  font-size: 1rem; /* Reduced font size */
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: transform 0.1s;
  white-space: nowrap; /* Prevent wrapping */

  &:active {
    transform: scale(0.95);
  }
`;

const KnowButton = styled(ControlButton)`
  background: #e50914;
  color: white;
  box-shadow: 0 4px 12px rgba(229, 9, 20, 0.3);
`;

const DontKnowButton = styled(ControlButton)`
  background: #333;
  color: #a3a3a3;
  
  &:hover {
    background: #444;
    color: white;
  }
`;

const ResultContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
`;

function QuizPage() {
  const navigate = useNavigate();
  const location = useLocation(); // Add useLocation for route changes
  const { speak } = useSmartTTS();
  const [words, setWords] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);

  // URL query param for mode
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode'); // 'review' or null

  useEffect(() => {
    setWords([]); // Reset words to avoid stale data
    setCurrentIndex(0); // Reset index
    setScore(0); // Reset score
    setShowResult(false); // Reset result view
    loadQuiz();
  }, [location.search]); // Depend on location.search

  const loadQuiz = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user) {
        navigate('/login');
        return;
      }
      const res = await apiService.getQuizWords(user.id, mode);
      if (res.success) {
        setWords(res.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = () => {
    if (!flipped) {
      setFlipped(true);
      // Auto-play meaning audio when flip? Optional.
      const w = words[currentIndex];
      if (w) {
        speak({ text: w.word, rate: 0.9 });
      }
    } else {
      setFlipped(false);
    }
  };

  const handleNext = async (known) => {
    if (known) setScore(score + 1);

    // Save status to backend
    const currentWord = words[currentIndex];
    if (currentWord && currentWord.id) {
      // known -> reviewNeeded: false
      // unknown -> reviewNeeded: true
      apiService.updateWordStatus(currentWord.id, {
        reviewNeeded: !known
      }).catch(err => console.error("Failed to update word status:", err));
    }

    setFlipped(false);
    setTimeout(() => {
      if (currentIndex < words.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setShowResult(true);
      }
    }, 200); // Wait for flip back
  };

  if (loading) return <QuizContainer><div style={{ margin: 'auto' }}>Loading Quiz...</div></QuizContainer>;

  if (words.length === 0) return (
    <QuizContainer>
      <Header>
        <BackButton onClick={() => navigate('/')}><ArrowLeft /></BackButton>
      </Header>
      <div style={{ margin: 'auto', textAlign: 'center', padding: 20 }}>
        <h2>{mode === 'review' ? '복습할 단어가 없습니다!' : '학습할 단어가 충분하지 않습니다.'}</h2>
        <p style={{ color: '#999', marginTop: 10 }}>
          {mode === 'review'
            ? '모르는 단어가 생기면 다시 오세요. 완벽합니다!'
            : '단어를 더 수집한 후 다시 시도해주세요.'}
        </p>
      </div>
    </QuizContainer>
  );

  if (showResult) {
    return (
      <QuizContainer>
        <Header>
          <BackButton onClick={() => navigate('/')}><X /></BackButton>
        </Header>
        <ResultContainer>
          <h1 style={{ fontSize: '4rem', color: '#e50914', marginBottom: 20 }}>{score} / {words.length}</h1>
          <h2 style={{ marginBottom: 40 }}>{mode === 'review' ? '복습 완료!' : '오늘의 학습 완료!'}</h2>

          <KnowButton onClick={() => window.location.reload()} style={{ width: '200px', flex: 'none' }}>
            <RefreshCw size={20} /> 다시 하기
          </KnowButton>
          <br />
          <DontKnowButton onClick={() => navigate('/word-history')} style={{ width: '200px', flex: 'none' }}>
            목록으로 이동
          </DontKnowButton>
        </ResultContainer>
      </QuizContainer>
    );
  }

  const currentWord = words[currentIndex];
  const progress = ((currentIndex) / words.length) * 100;

  // Extract first meaning/example
  const meaning = currentWord.meanings && currentWord.meanings[0] ? (currentWord.meanings[0].meaning || currentWord.meanings[0].의미1) : currentWord.translation;
  const example = currentWord.meanings && currentWord.meanings[0] ? (currentWord.meanings[0].example || currentWord.meanings[0].예문1) : currentWord.exampleSentence;

  return (
    <QuizContainer>
      <Header>
        <BackButton onClick={() => navigate('/')}><Check size={24} title="Finish" /></BackButton>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {mode === 'review' && (
            <ModeBadge>
              <BrainCircuit size={14} /> 오답 복습
            </ModeBadge>
          )}
          <span style={{ fontWeight: 600 }}>{currentIndex + 1} / {words.length}</span>
        </div>
        <div style={{ width: 40 }}></div> {/* Check balance */}
      </Header>
      <ProgressBar><ProgressFill $percent={progress} /></ProgressBar>

      <CardContainer>
        <Card onClick={handleCardClick} $flipped={flipped}>
          <CardFace>
            <span style={{ position: 'absolute', top: 20, right: 20, background: '#333', padding: '4px 8px', borderRadius: 4, fontSize: '0.8rem' }}>Click to Flip</span>
            <WordTitle>{currentWord.word}</WordTitle>
            <Volume2 size={32} color="#666" style={{ marginTop: 20 }} />
          </CardFace>

          <CardBack>
            <div style={{ alignSelf: 'flex-start', color: '#666', fontSize: '0.9rem', marginBottom: 'auto' }}>
              {currentWord.word}
            </div>
            <MeaningText>{meaning || '뜻 없음'}</MeaningText>
            {example && (
              <ExampleText>
                "{example}"
                <button onClick={(e) => {
                  e.stopPropagation();
                  speak({ text: example, rate: 0.9 });
                }} style={{ background: 'none', border: 'none', marginLeft: 8, cursor: 'pointer', color: '#e50914', verticalAlign: 'middle' }}>
                  <Volume2 size={16} />
                </button>
              </ExampleText>
            )}
            <HintText>터치해서 닫기</HintText>
          </CardBack>
        </Card>
      </CardContainer>

      <Controls>
        <DontKnowButton onClick={() => handleNext(false)}>
          모르겠어요
        </DontKnowButton>
        <KnowButton onClick={() => handleNext(true)}>
          알고 있어요
        </KnowButton>
      </Controls>
    </QuizContainer>
  );
}

export default QuizPage;
